import { shopifyIntegrationConfigSchema } from "schemas/shopifyIntegration";
import { stripeConnectConfigSchema } from "schemas/stripeConnect";
import { z } from "zod";

export const integrationSchema = z
  .object({
    id: z.uuid(),
    type: z.enum([
      "ShopifyIntegrationConfig",
      "StripeConnect",
      "Klaviyo",
      "GA4",
      "GoogleTagManager",
      "Hotjar",
      "MicrosoftClarity",
      "Snapchat",
      "TikTok",
      "Triplewhale",
      "Pinterest",
      "Reddit",
      "Meta",
    ]),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    deletedAt: z.coerce.date().nullish(),
    workspaceId: z.uuid(),
    shopifyIntegrationConfig: shopifyIntegrationConfigSchema.nullable(),
    stripeConnectConfig: stripeConnectConfigSchema.nullable(),
    projectId: z.uuid().nullish(),
    config: z.any().optional(),
  })
  .describe("Integration");

export const klaviyoConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Public API Key is required"),
  })
  .describe("KlaviyoConfig");

export const ga4ConfigSchema = z
  .object({
    integrationKey: z
      .string()
      .regex(/^G-[\dA-Z]{10}$/, "Measurement ID must be in format G-XXXXXXXXXX")
      .min(1, "Measurement ID is required"),
  })
  .describe("GA4Config");

export const gtmConfigSchema = z
  .object({
    integrationKey: z
      .string()
      .regex(/^GTM-[\dA-Z]{7,8}$/, "Container ID must be in format GTM-XXXXXXX")
      .min(1, "Container ID is required"),
  })
  .describe("GTMConfig");

export const hotjarConfigSchema = z
  .object({
    integrationKey: z
      .string()
      .regex(/^\d+$/, "Site ID must be numeric")
      .min(1, "Site ID is required"),
  })
  .describe("HotjarConfig");

export const clarityConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Project ID is required"),
  })
  .describe("ClarityConfig");

export const snapchatConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Pixel ID is required"),
  })
  .describe("SnapchatConfig");

export const tiktokConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Pixel ID is required"),
  })
  .describe("TikTokConfig");

export const pinterestConfigSchema = z
  .object({
    integrationKey: z
      .string()
      .regex(/^\d+$/, "Tag ID must be numeric")
      .min(1, "Tag ID is required"),
  })
  .describe("PinterestConfig");

export const redditConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Pixel ID is required"),
  })
  .describe("RedditConfig");

export const metaConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Pixel ID is required"),
  })
  .describe("MetaConfig");

const baseIntegrationFields = integrationSchema.omit({
  config: true,
  type: true,
  shopifyIntegrationConfig: true,
});

export const klaviyoIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Klaviyo"),
    config: klaviyoConfigSchema,
  })
  .describe("KlaviyoIntegration");

export const shopifyIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("ShopifyIntegrationConfig"),
    config: shopifyIntegrationConfigSchema,
  })
  .describe("ShopifyIntegration");

export const stripeConnectIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("StripeConnect"),
    config: stripeConnectConfigSchema,
  })
  .describe("StripeConnectIntegration");

export const ga4IntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("GA4"),
    config: ga4ConfigSchema,
  })
  .describe("GA4Integration");

export const gtmIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("GoogleTagManager"),
    config: gtmConfigSchema,
  })
  .describe("GTMIntegration");

export const hotjarIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Hotjar"),
    config: hotjarConfigSchema,
  })
  .describe("HotjarIntegration");

export const clarityIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("MicrosoftClarity"),
    config: clarityConfigSchema,
  })
  .describe("ClarityIntegration");

export const snapchatIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Snapchat"),
    config: snapchatConfigSchema,
  })
  .describe("SnapchatIntegration");

export const tiktokIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("TikTok"),
    config: tiktokConfigSchema,
  })
  .describe("TikTokIntegration");

export const triplewhaleIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Triplewhale"),
    config: tiktokConfigSchema,
  })
  .describe("TriplewhaleIntegration");

export const pinterestIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Pinterest"),
    config: pinterestConfigSchema,
  })
  .describe("PinterestIntegration");

export const redditIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Reddit"),
    config: redditConfigSchema,
  })
  .describe("RedditIntegration");

export const metaIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Meta"),
    config: metaConfigSchema,
  })
  .describe("MetaIntegration");

export const reploIntegrationSchema = z
  .discriminatedUnion("type", [
    klaviyoIntegrationSchema,
    ga4IntegrationSchema,
    gtmIntegrationSchema,
    hotjarIntegrationSchema,
    clarityIntegrationSchema,
    snapchatIntegrationSchema,
    tiktokIntegrationSchema,
    triplewhaleIntegrationSchema,
    pinterestIntegrationSchema,
    redditIntegrationSchema,
    metaIntegrationSchema,
    shopifyIntegrationSchema,
    stripeConnectIntegrationSchema,
  ])
  .describe("ReploIntegration");
